import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams, useSearchParams } from 'react-router';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { mfaVerifyRequest } from '@/lib/auth/allauth';

import { isSafeRedirectPath } from '../safeRedirect';
import BaseLogin from './BaseLogin';

const MFAVerifySchema = z.object({
  token: z.string().min(6, 'Enter the 6-digit code'),
  user_id: z.number(),
});

type MFAVerifySchemaType = z.infer<typeof MFAVerifySchema>;

export default function MFAVerify() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const from = searchParams.get('from');

  const [errorMessage, setErrorMessage] = useState('');

  const form = useForm<MFAVerifySchemaType>({
    resolver: zodResolver(MFAVerifySchema),
    defaultValues: {
      user_id: Number(userId),
      token: '',
    },
  });

  const changeMutation = useMutation({
    mutationFn: async (data: MFAVerifySchemaType) => {
      setErrorMessage('');
      return await mfaVerifyRequest({
        token: data.token,
        user_id: data.user_id,
      });
    },
    onSuccess({ data }) {
      if (data?.status === 200) {
        // Full-page navigation (not SPA nav): after MFA re-auth mid-session, the SPA's
        // auth/CSRF state is stale; a fresh load reconciles the new session (matches the
        // behavior of a manual hard refresh) and avoids a 401 on the destination page.
        window.location.assign(from && isSafeRedirectPath(from) ? from : '/');
      } else if (data?.status === 400) {
        setErrorMessage(
          data?.message ||
            'An error occurred during verification. Please try again.'
        );
      }
    },
    onError(error) {
      setErrorMessage(
        error.message ||
          'An error occurred during verification. Please try again.'
      );
    },
  });

  function onSubmit(data: MFAVerifySchemaType) {
    changeMutation.mutate(data);
  }

  const backToLoginHref =
    from && isSafeRedirectPath(from)
      ? '/auth/login?from=' + encodeURIComponent(from)
      : '/auth/login';

  return (
    <BaseLogin
      renderComponent={
        <Form {...form}>
          {errorMessage ? (
            <div className="mb-4 rounded-md bg-red-50 p-3">
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          ) : null}
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-auto flex max-w-md flex-col gap-4"
          >
            <FormField
              control={form.control}
              name="token"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Token</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      placeholder="Enter your 6-digit code"
                      autoComplete="one-time-code"
                      {...field}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <Button type="submit" className="bg-blue-600">
              Verify
            </Button>
          </form>

          <div className="mt-4 flex justify-center">
            <Link to={backToLoginHref} className="text-blue-500 underline">
              Back to Login
            </Link>
          </div>
        </Form>
      }
      title="MFA Verification Required"
      description="Please enter the verification code sent to your email"
    />
  );
}
